import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import i18next from "i18next";
import { createOfflineToastLink } from "../exported/api/graphql/offlineToastLink";
import { createEmergencyCloudLink } from "../exported/api/graphql/emergencyCloudLink";
import { isEmergencySession } from "../exported/shared/utils/emergencySession";
import { createNotify } from "../shell-handle/notify";
import type { AuthApi } from "../shell-handle/auth";

// The shell never initialises i18next — see UserSettings.tsx, where the language
// switch is store-only for exactly this reason. Its keys are their own English
// text, so an uninitialised instance falls back to correct copy instead of
// rendering a key. Importing exported/i18next.config here instead would drag the
// MFEs' i18n bundle into the shell graph, which the shell cannot resolve.
const OFFLINE_MESSAGE = "No internet connection";
const offlineMessage = () =>
  i18next.isInitialized ? i18next.t(OFFLINE_MESSAGE) : OFFLINE_MESSAGE;

// One instance per client, so each client dedupes its own toasts; they all route
// through the same module-level toast sink.
function buildOfflineToastLink() {
  return createOfflineToastLink(createNotify(), offlineMessage);
}

function buildEmergencyCloudLink() {
  return createEmergencyCloudLink(isEmergencySession);
}

function buildAuthLink(auth: AuthApi) {
  return setContext(async (_, prevContext) => {
    const { headers, forceRefresh } = prevContext as {
      headers?: Record<string, string>;
      forceRefresh?: boolean;
    };
    let token = "";
    try {
      token = await auth.getToken({ forceRefresh: !!forceRefresh });
    } catch {
      /* unauth, request may still proceed for /login flows */
    }
    return {
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  });
}

function buildErrorLink(auth: AuthApi) {
  return onError(({ networkError, operation, forward }) => {
    const status = (networkError as { statusCode?: number } | null | undefined)
      ?.statusCode;
    if (status !== 401 && status !== 403) return;
    const ctx = operation.getContext() as { didRetryAuth?: boolean };
    if (ctx.didRetryAuth) return;
    return new Observable((observer) => {
      void auth
        .getToken({ forceRefresh: true })
        .then(() => {
          operation.setContext({
            ...ctx,
            didRetryAuth: true,
            forceRefresh: true,
          });
          forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch((err) => observer.error(err));
    });
  });
}

export function createApolloClient(auth: AuthApi): ApolloClient<unknown> {
  const httpLink = new HttpLink({
    uri: import.meta.env.VITE_NEMO_AUTH_GQL_URL,
  });
  return new ApolloClient({
    link: from([
      buildEmergencyCloudLink(),
      buildErrorLink(auth),
      buildAuthLink(auth),
      buildOfflineToastLink(),
      httpLink,
    ]),
    cache: new InMemoryCache(),
  });
}

export function createCloudApolloClient(auth: AuthApi): ApolloClient<unknown> {
  const httpLink = new HttpLink({ uri: import.meta.env.VITE_CLOUD_GQL_URL });
  return new ApolloClient({
    link: from([
      buildEmergencyCloudLink(),
      buildErrorLink(auth),
      buildAuthLink(auth),
      buildOfflineToastLink(),
      httpLink,
    ]),
    cache: new InMemoryCache({ addTypename: false }),
  });
}
